import { FIRMA_CONTABLE, pymePrice, type PoolPack, type RncTypeId } from "./plans.ts";

export type FirmaQuoteInput = {
  basico: number;
  plus: number;
  empresarial: number;
  packs?: PoolPack[];
};

export type FirmaBreakdownLine = {
  label: string;
  qty: number;
  unit: number;
  total: number;
};

export type FirmaQuote = {
  monthly: number;
  ecfIncluded: number;
  setup: number;
  perClient: number;
  breakdown: FirmaBreakdownLine[];
  totalClients: number;
  individualMonthly: number;
  savingsRatio: number;
};

function nonNegInt(value: number) {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

function rncType(id: RncTypeId) {
  const found = FIRMA_CONTABLE.rnc_types.find((item) => item.id === id);
  if (!found) throw new Error(`RNC type ${id} missing`);
  return found;
}

function setupPrice() {
  return FIRMA_CONTABLE.addons.find((item) => item.id === "rnc_setup")?.price ?? 0;
}

export function priceFirmaContable(input: FirmaQuoteInput): FirmaQuote {
  const basico = nonNegInt(input.basico);
  const plus = nonNegInt(input.plus);
  const empresarial = nonNegInt(input.empresarial);
  const totalClients = basico + plus + empresarial;

  const basicoType = rncType("basico");
  const plusType = rncType("plus");
  const empType = rncType("empresarial");

  let monthly = FIRMA_CONTABLE.base_price;
  let ecfIncluded = FIRMA_CONTABLE.included_ecf;
  const breakdown: FirmaBreakdownLine[] = [
    {
      label: "Base",
      qty: 1,
      unit: FIRMA_CONTABLE.base_price,
      total: FIRMA_CONTABLE.base_price,
    },
  ];

  let extraBasicoCost = 0;
  let extraBasicoQty = 0;
  for (let n = FIRMA_CONTABLE.included_rnc + 1; n <= basico; n++) {
    const tier = (basicoType.tiers || []).find((t) => n >= t.from && (t.to === null || n <= t.to));
    if (!tier) continue;
    extraBasicoCost += tier.price;
    extraBasicoQty += 1;
    ecfIncluded += basicoType.ecf;
  }
  monthly += extraBasicoCost;
  if (extraBasicoQty > 0) {
    breakdown.push({
      label: basicoType.label,
      qty: extraBasicoQty,
      unit: extraBasicoCost / extraBasicoQty,
      total: extraBasicoCost,
    });
  }

  if (plus > 0 && plusType.price != null) {
    const total = plus * plusType.price;
    monthly += total;
    ecfIncluded += plus * plusType.ecf;
    breakdown.push({ label: plusType.label, qty: plus, unit: plusType.price, total });
  }

  if (empresarial > 0 && empType.price != null) {
    const total = empresarial * empType.price;
    monthly += total;
    ecfIncluded += empresarial * empType.ecf;
    breakdown.push({ label: empType.label, qty: empresarial, unit: empType.price, total });
  }

  const individualMonthly =
    basico * pymePrice("starter") + plus * pymePrice("growth") + empresarial * pymePrice("pro");
  const savingsRatio = individualMonthly > 0 ? (individualMonthly - monthly) / individualMonthly : 0;

  return {
    monthly,
    ecfIncluded,
    setup: totalClients * setupPrice(),
    perClient: totalClients === 0 ? 0 : monthly / totalClients,
    breakdown,
    totalClients,
    individualMonthly,
    savingsRatio,
  };
}

export function packUnit(pack: PoolPack) {
  return pack.price / pack.ecf;
}
